import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { useModal } from '~/eds/src/components/hooks/useModal';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { RadioGroup } from '~/eds/src/components/RadioGroup/RadioGroup';
import { Select } from '~/eds/src/components/Select/Select';
import { Form } from '~/eds/src/modules/Form/Form';
import { HttpStatusCodeType } from '~/enums/HttpStatusCodeType';
import { api } from '~/redux/api/createApi';
import { Nullable } from '~/types/Nullable';
import { Workflow } from '~/types/Workflow';
import { capitalize } from '~/utils/strings';

import { getWorkflowStatusLabels } from './utils';

interface DuplicateWorkflowModalProps {
  workflow: Nullable<Workflow>;
  onHide?: () => void;
}

type CopyType = 'new' | 'onto';

type InitialStateType = {
  copyType: {
    value: CopyType | null;
  };
  targetWorkflow: {
    value: string | null;
  };
};

export const useDuplicateWorkflowModal = ({
  workflow,
  onHide,
}: DuplicateWorkflowModalProps) => {
  const intl = useIntl();
  const { toast } = useToast();

  const [isValid, setIsValid] = useState(true);
  const [copyType, setCopyType] = useState<CopyType | null>(null);
  const [targetWorkflowId, setTargetWorkflowId] = useState<string | null>(null);

  const { data: workflows = [], isFetching: isFetchingWorkflows } =
    api.endpoints.getWorkflows.useQuery({});

  const workflowOptions = useMemo(() => {
    const workflowStatusLabels = getWorkflowStatusLabels(intl);
    return workflows
      .filter((w) => w.id !== workflow?.id)
      .map((w) => {
        const statusLabel =
          workflowStatusLabels[w.status] ?? capitalize(w.status);
        return {
          value: w.id,
          name: w.name,
          label: w.name,
          description: statusLabel,
        };
      });
  }, [workflows, workflow?.id, intl]);

  const fields = useMemo(() => {
    const copyOntoDescriptionText = intl.formatMessage({
      id: 'UseDuplicateWorkflowModal.copyOntoDescription',
      defaultMessage:
        'Add the copy as a new version of an existing workflow.\nNote: if the workflow is in draft, the copy will replace the latest draft.',
      description:
        'Description text for "Attach to an existing workflow" option',
    });

    const copyOntoDescription = (
      <>
        {copyOntoDescriptionText.split('\n')[0]}
        <br />
        {copyOntoDescriptionText.split('\n')[1]}
      </>
    );

    const copyAsNewLabel = intl.formatMessage({
      id: 'UseDuplicateWorkflowModal.copyAsNewLabel',
      defaultMessage: 'Create a new workflow',
      description: 'Label for create a new workflow option',
    });

    const copyOntoLabel = intl.formatMessage({
      id: 'UseDuplicateWorkflowModal.copyOntoLabel',
      defaultMessage: 'Attach to an existing workflow',
      description: 'Label for attach to an existing workflow option',
    });

    const selectWorkflowLabel = intl.formatMessage({
      id: 'UseDuplicateWorkflowModal.selectWorkflowLabel',
      defaultMessage: 'Select workflow',
      description: 'Label for select workflow dropdown',
    });

    const baseFields: Record<string, any> = {
      copyType: {
        order: 1,
        key: 'copyType',
        type: 'field' as const,
        name: 'copyType',
        input: RadioGroup,
        inputProps: {
          options: [
            { value: 'new', label: copyAsNewLabel },
            {
              value: 'onto',
              label: copyOntoLabel,
              description: copyOntoDescription,
            },
          ],
          columns: 1,
        },
      },
    };

    if (copyType === 'onto') {
      baseFields.targetWorkflow = {
        order: 2,
        key: 'targetWorkflow',
        type: 'field' as const,
        name: 'targetWorkflow',
        label: selectWorkflowLabel,
        input: Select,
        inputProps: {
          options: workflowOptions,
          isClearable: false,
          enablePortal: false,
        },
      };
    }

    return baseFields;
  }, [copyType, workflowOptions, intl]);

  const initialState: InitialStateType = {
    copyType: {
      value: null,
    },
    targetWorkflow: {
      value: null,
    },
  };

  const [duplicateWorkflow, { isLoading: isCopying }] =
    api.endpoints.duplicateWorkflow.useMutation();

  const handleDuplicate = async () => {
    if (!workflow) return;

    if (copyType === 'onto' && !targetWorkflowId) {
      toast({
        status: 'danger',
        message: intl.formatMessage({
          id: 'UseDuplicateWorkflowModal.selectTargetWorkflow',
          defaultMessage: 'Please select a target workflow',
          description: 'Error when target workflow is not selected',
        }),
      });
      return;
    }

    try {
      await duplicateWorkflow({
        id: workflow.id,
        targetWorkflowId:
          copyType === 'onto' ? targetWorkflowId || undefined : undefined,
      }).unwrap();

      let successMessage = '';
      if (copyType === 'onto') {
        const targetName =
          workflows.find((w) => w.id === targetWorkflowId)?.name || '';
        successMessage = intl.formatMessage(
          {
            id: 'workflow.duplicateSuccessOnto',
            defaultMessage: 'Copied and attached to {workflowName}',
          },
          { workflowName: targetName },
        );
      } else {
        successMessage = intl.formatMessage(
          {
            id: 'workflow.duplicateSuccessNew',
            defaultMessage: 'Created Copy of {workflowName}',
          },
          { workflowName: workflow?.name },
        );
      }

      toast({
        status: 'success',
        message: successMessage,
      });
      hide();
      if (onHide) onHide();
    } catch (error: any) {
      const statusCode = error?.response?.status || error?.status;

      let errorMessage = '';

      if (
        statusCode === HttpStatusCodeType.Forbidden ||
        statusCode === HttpStatusCodeType.Unauthorized
      ) {
        errorMessage = intl.formatMessage({
          id: 'workflow.duplicateErrorPermission',
          defaultMessage:
            'Copy Failed: Access is denied. Contact Company Admin for Support.',
        });
      } else if (statusCode === HttpStatusCodeType.NotFound) {
        errorMessage = intl.formatMessage({
          id: 'workflow.duplicateErrorNotFound',
          defaultMessage: 'Copy Failed: The workflow was not found.',
        });
      }
      // Network/connection errors (no status code)
      else if (!statusCode) {
        errorMessage = intl.formatMessage({
          id: 'workflow.duplicateError',
          defaultMessage: 'Copy Failed: Unexpected error. Try again later.',
        });
      } else {
        errorMessage = intl.formatMessage({
          id: 'workflow.duplicateErrorServer',
          defaultMessage: 'Copy Failed: Unexpected error. Contact Support.',
        });
      }

      toast({
        status: 'danger',
        message: errorMessage,
      });
    }
  };

  const [modal, show, hide] = useModal({
    title: intl.formatMessage(
      {
        id: 'UseDuplicateWorkflowModal.modalTitle',
        defaultMessage: 'Copy {workflowName}',
        description: 'Modal title for duplicating a workflow',
      },
      { workflowName: workflow?.name },
    ),
    onHide: () => {
      setCopyType(null);
      setTargetWorkflowId(null);
      if (onHide) onHide();
    },
    primaryAction: {
      onClick: handleDuplicate,
      text: intl.formatMessage({
        id: 'UseDuplicateWorkflowModal.duplicateButton',
        defaultMessage: 'Make a copy',
        description: 'Button text for duplicating workflow',
      }),
      isLoading: isCopying || isFetchingWorkflows,
      disabled:
        !isValid || !copyType || (copyType === 'onto' && !targetWorkflowId),
      tooltip: isValid
        ? undefined
        : intl.formatMessage({
            id: 'UseDuplicateWorkflowModal.fixErrorsTooltip',
            defaultMessage: 'Please fix the errors before submitting',
            description: 'Tooltip shown when form has validation errors',
          }),
    },
    children: (
      <ContentContainer
        loadingContent={{
          isLoading: isFetchingWorkflows,
          message: intl.formatMessage({
            id: 'UseDuplicateWorkflowModal.loadingWorkflows',
            defaultMessage: 'Loading workflows...',
            description: 'Loading message while fetching workflows',
          }),
        }}
      >
        <Form<InitialStateType>
          fields={fields}
          onChange={(updatedState) => {
            const newCopyType = updatedState.state.copyType
              .value as unknown as CopyType | null;
            const newTargetWorkflow = updatedState.state.targetWorkflow
              ? (updatedState.state.targetWorkflow.value as unknown as
                  | string
                  | null)
              : null;

            setCopyType(newCopyType);
            setTargetWorkflowId(newTargetWorkflow || null);

            if (newCopyType === 'new') {
              setTargetWorkflowId(null);
            }
          }}
          state={initialState as any}
          onValidate={(updatedState) => {
            const errors: string[] = [];

            const copyTypeValue = updatedState.state.copyType
              .value as unknown as CopyType | null;
            if (!copyTypeValue) {
              errors.push('copyType');
            }

            if (copyTypeValue === 'onto') {
              const targetValue = updatedState.state.targetWorkflow
                ? (updatedState.state.targetWorkflow.value as unknown as
                    | string
                    | null)
                : null;
              if (!targetValue) {
                errors.push('targetWorkflow');
              }
            }

            setIsValid(errors.length === 0);
          }}
        />
      </ContentContainer>
    ),
  });

  return [modal, show, hide] as const;
};
